<script lang="ts" generics="T extends string | number">
  import { Select } from 'bits-ui';

  let {
    id,
    value = $bindable(),
    options,
    disabled = false,
    placeholder = 'Choose…',
    onchange,
    'aria-label': label,
    'aria-describedby': describedby,
    'aria-invalid': invalid,
  }: {
    id: string;
    value: T;
    options: readonly { value: T; label: string }[];
    disabled?: boolean;
    placeholder?: string;
    onchange?: (value: T) => void;
    'aria-label'?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
  } = $props();

  let open = $state(false);
  const items = $derived(options.map((option) => ({ value: encode(option.value), label: option.label })));
  const selectedValue = $derived(options.some((option) => option.value === value) ? encode(value) : '');
  const selectedLabel = $derived(options.find((option) => option.value === value)?.label);

  function encode(option: string | number): string {
    return `${typeof option}:${option}`;
  }

  function choose(encoded: string): void {
    const option = options.find((candidate) => encode(candidate.value) === encoded);
    if (!option || option.value === value) return;
    value = option.value;
    onchange?.(value);
  }

  $effect(() => {
    if (disabled || options.length === 0) open = false;
  });
</script>

<Select.Root type="single" value={selectedValue} onValueChange={choose} bind:open {disabled} {items}>
  <Select.Trigger
    {id}
    class="select-trigger"
    aria-label={label}
    aria-describedby={describedby}
    aria-invalid={invalid}
    onpointerup={(event) => {
      // Bits prevents touch's native focus; keep the app's trigger-focus policy.
      if (!disabled && event.pointerType === 'touch') event.currentTarget.focus({ preventScroll: true });
    }}
  >
    <span>{selectedLabel ?? placeholder}</span><span aria-hidden="true">▾</span>
  </Select.Trigger>
  <Select.Portal to="#app, #showcase">
    <Select.Content
      id={`${id}-listbox`}
      class="surface select-popup"
      sideOffset={4}
      collisionPadding={8}
      strategy="fixed"
      preventScroll={false}
      onmousedown={(event) => event.preventDefault()}
      aria-label={label ? `${label} options` : undefined}
      aria-labelledby={label ? undefined : id}
    >
      <Select.Viewport class="select-viewport">
        {#each options as option, index (encode(option.value))}
          <Select.Item
            id={`${id}-option-${index}`}
            value={encode(option.value)}
            label={option.label}
            data-app-value={option.value}
          >
            {#snippet children({ selected })}
              <span>{option.label}</span><span aria-hidden="true">{selected ? '✓' : ''}</span>
            {/snippet}
          </Select.Item>
        {/each}
      </Select.Viewport>
    </Select.Content>
  </Select.Portal>
</Select.Root>

<style>
  :global(.select-trigger) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-width: 0;
    min-height: 44px;
    text-align: left;
    cursor: pointer;
  }
  :global(.select-trigger:disabled) {
    cursor: default;
  }
  :global(.surface.select-popup) {
    --surface-radius: var(--theme-input-radius);
    z-index: 120;
    width: var(--bits-select-anchor-width);
    max-width: calc(100vw - 16px);
    max-height: min(264px, var(--bits-select-content-available-height));
    padding: 4px;
    overflow: hidden;
  }
  :global(.select-viewport) {
    max-height: inherit;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  :global([data-select-item]) {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    align-items: center;
    min-height: 44px;
    padding: 8px;
    box-sizing: border-box;
    cursor: pointer;
    border-radius: var(--theme-chip-radius);
  }
  :global([data-select-item][data-selected]) {
    background: var(--theme-selected);
    color: var(--theme-selected-text);
  }
  :global([data-select-item][data-highlighted]) {
    outline: var(--theme-focus-width) solid var(--theme-focus);
    outline-offset: -2px;
  }
</style>
